import React from 'react';
import './App.css';
import UniversalHeader from './UniversalHeader';
import { supabase } from './supabaseClient';

function formatWhen(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function initials(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
}

function Accounts() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [search, setSearch] = React.useState('');

  const loadData = React.useCallback(async () => {
    if (!supabase) {
      setError('Supabase is not configured.');
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    const { data, error: loadError } = await supabase
      .from('customer_accounts')
      .select('id, full_name, email, created_at')
      .order('created_at', { ascending: false });

    if (loadError) {
      setError(
        loadError.message ||
          'Could not load accounts. Run the latest sql/add_customer_accounts_supabase.sql in Supabase (section 6 — select policies).'
      );
      setRows([]);
    } else {
      setRows(Array.isArray(data) ? data : []);
    }
    setLoading(false);
  }, []);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const filtered = rows.filter((r) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    const name = (r.full_name || '').toLowerCase();
    const email = (r.email || '').toLowerCase();
    return name.includes(term) || email.includes(term);
  });

  return (
    <div className="tenants-root">
      <UniversalHeader />

      <div className="tenants-shell">
        <header className="tenants-header">
          <div>
            <h1 className="tenants-title">Accounts</h1>
            <p className="tenants-subtitle">
              People who signed up on the public site (customer sign-up at /sign-up). Passwords are
              never shown here.
            </p>
          </div>
          <div className="tenants-actions">
            <button type="button" className="btn-ghost tenants-btn" onClick={loadData}>
              Refresh
            </button>
          </div>
        </header>

        {error ? (
          <p className="properties-save-error" role="alert">
            {error}
          </p>
        ) : null}

        <section className="tenants-toolbar">
          <div className="tenants-search">
            <div className="input-shell tenants-search-shell">
              <div className="input-icon">🔍</div>
              <input
                className="input-element"
                type="text"
                placeholder="Search by name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </section>

        <main className="tenants-layout">
          <section className="tenants-list">
            <div className="tenants-list-header">
              <span className="tenants-list-label">Customer accounts</span>
              <span className="tenants-list-count">
                {loading ? '…' : `${filtered.length} shown`}
              </span>
            </div>

            {loading ? (
              <p className="properties-list-empty">Loading…</p>
            ) : (
              <div className="tenants-grid">
                {filtered.length === 0 ? (
                  <p className="properties-list-empty">
                    {rows.length === 0 ? (
                      <>
                        No customer sign-ups yet. When visitors register at{' '}
                        <code>/sign-up</code>, they appear here. If this errors, apply section 6 of{' '}
                        <code>sql/add_customer_accounts_supabase.sql</code> in Supabase.
                      </>
                    ) : (
                      'No accounts match your search.'
                    )}
                  </p>
                ) : (
                  filtered.map((r) => (
                    <article className="tenants-card" key={r.id}>
                      <div className="tenants-card-main">
                        <div className="tenants-avatar">
                          <span>{initials(r.full_name)}</span>
                        </div>
                        <div className="tenants-main-text">
                          <h2 className="tenants-card-name">{r.full_name?.trim() || '—'}</h2>
                          <p className="tenants-card-contact">{r.email || '—'}</p>
                          <p className="tenants-card-property" style={{ marginTop: 6 }}>
                            Public site customer
                          </p>
                        </div>
                      </div>
                      <div className="tenants-card-meta">
                        <div className="tenants-meta-block">
                          <span className="tenants-meta-label">Signed up</span>
                          <span className="tenants-meta-value">{formatWhen(r.created_at)}</span>
                        </div>
                      </div>
                    </article>
                  ))
                )}
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}

export default Accounts;
